import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  ResponseErrorPanel,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import useAsync from 'react-use/esm/useAsync';
import {
  BITBUCKET_PIPELINES_BRANCH_ANNOTATION,
  BITBUCKET_PROJECT_SLUG_ANNOTATION,
  filterByBranch,
  PipelineRun,
  RunRow,
  RunStatus,
  toRunRow,
} from './mapRun';

export const isBitbucketPipelinesAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[BITBUCKET_PROJECT_SLUG_ANNOTATION]);

const PAGE_SIZE = 20;

const statusIcon: Record<RunStatus, (label: string) => JSX.Element> = {
  ok: label => <StatusOK>{label}</StatusOK>,
  error: label => <StatusError>{label}</StatusError>,
  running: label => <StatusRunning>{label}</StatusRunning>,
  pending: label => <StatusPending>{label}</StatusPending>,
  aborted: label => <StatusAborted>{label}</StatusAborted>,
};

const columns: TableColumn<RunRow>[] = [
  {
    title: '#',
    field: 'number',
    width: '6em',
    render: row => <Link to={row.url}>#{row.number}</Link>,
  },
  {
    title: 'Status',
    field: 'statusLabel',
    render: row => statusIcon[row.status](row.statusLabel),
  },
  { title: 'Branch', field: 'ref' },
  { title: 'Commit', field: 'commit' },
  { title: 'Trigger', field: 'trigger' },
  { title: 'By', field: 'creator' },
  {
    title: 'Started',
    field: 'startedAt',
    render: row => row.startedAt.toLocaleString(),
  },
  { title: 'Duration', field: 'duration' },
];

export const BitbucketPipelinesCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const projectSlug =
    entity.metadata.annotations?.[BITBUCKET_PROJECT_SLUG_ANNOTATION] ?? '';
  const branch =
    entity.metadata.annotations?.[BITBUCKET_PIPELINES_BRANCH_ANNOTATION];

  const { value, loading, error } = useAsync(async () => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');
    const url = `${proxyUrl}/bitbucket/api/repositories/${projectSlug}/pipelines/?sort=-created_on&pagelen=${PAGE_SIZE}`;
    const response = await fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(
        `Bitbucket returned ${response.status} for ${projectSlug}`,
      );
    }
    const body = (await response.json()) as { values: PipelineRun[] };
    return filterByBranch(body.values, branch).map(run =>
      toRunRow(run, projectSlug),
    );
  }, [projectSlug, branch]);

  if (error) {
    return (
      <InfoCard title="Bitbucket Pipelines">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  const subtitle = branch ? `${projectSlug}, branch ${branch}` : projectSlug;

  return (
    <Table<RunRow>
      title="Bitbucket Pipelines"
      subtitle={subtitle}
      isLoading={loading}
      columns={columns}
      data={value ?? []}
      options={{ paging: false, search: false, padding: 'dense' }}
      emptyContent={
        <Typography variant="body2" style={{ padding: 16 }}>
          No pipeline runs in the last {PAGE_SIZE}
          {branch ? ` for branch ${branch}` : ''}.
        </Typography>
      }
    />
  );
};
